import { useEffect, useState } from "react";
import api, { downloadCsv } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, num, dt } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wallet, TrendingUp, IndianRupee, Percent, Download } from "lucide-react";
import { toast } from "sonner";

export default function Earnings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/transactions", { params: { status: "success" } }).then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  const isMerchant = user.role === "merchant";
  const isDist = user.role === "distributor";

  const cards = isMerchant
    ? [
        { label: "Gross Collected", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "earn-gross" },
        { label: "Fees Paid", value: inr(data.platform_fee), icon: Percent, accent: "warning", testid: "earn-fee" },
        { label: "Net Settlement", value: inr(data.merchant_net), sub: "Credited to your bank", icon: Wallet, accent: "success", testid: "earn-net" },
      ]
    : isDist
    ? [
        { label: "Downline Volume", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "earn-gross" },
        { label: "My Commission", value: inr(data.distributor_earning), sub: "Share of platform fees", icon: Wallet, accent: "success", testid: "earn-commission" },
        { label: "Successful Txns", value: num(data.success_count), icon: TrendingUp, accent: "secondary", testid: "earn-count" },
      ]
    : [
        { label: "Gross Volume", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "earn-gross" },
        { label: "Total Platform Fee", value: inr(data.platform_fee), icon: Percent, accent: "warning", testid: "earn-fee" },
        { label: "Distributor Payouts", value: inr(data.distributor_earning), icon: Wallet, accent: "secondary", testid: "earn-payouts" },
        { label: "Platform Revenue", value: inr(data.superadmin_earning), sub: "Your net earnings", icon: TrendingUp, accent: "success", testid: "earn-revenue" },
      ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{isMerchant ? "Settlements" : "Earnings"}</h2>
          <p className="text-sm text-muted-foreground">Commission ledger, computed on every successful collection.</p>
        </div>
        <Button variant="outline" data-testid="export-settlements-button"
          onClick={() => downloadCsv("/transactions/export", { status: "success" }, `woltpe_settlements_${Date.now()}.csv`).catch(() => toast.error("Export failed"))}>
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className={`grid gap-4 ${isMerchant || isDist ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <Card className="p-4 overflow-x-auto">
        <h3 className="font-heading font-semibold mb-3 px-1">Settled transactions</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Platform fee</TableHead>
              {!isMerchant && <TableHead className="text-right">Distributor</TableHead>}
              {user.role === "super_admin" && <TableHead className="text-right">Admin net</TableHead>}
              <TableHead className="text-right">{isMerchant ? "Net credited" : "Merchant net"}</TableHead>
              <TableHead>Settled</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {txns.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No settlements yet.</TableCell></TableRow>}
            {txns.map((t) => (
              <TableRow key={t.id} data-testid={`earn-row-${t.order_id}`}>
                <TableCell className="font-mono text-xs">{t.order_id}</TableCell>
                <TableCell className="text-right font-mono">{inr(t.amount)}</TableCell>
                <TableCell className="text-right font-mono text-warning">{inr(t.platform_fee)}</TableCell>
                {!isMerchant && <TableCell className="text-right font-mono text-chart-2">{inr(t.distributor_earning)}</TableCell>}
                {user.role === "super_admin" && <TableCell className="text-right font-mono text-primary">{inr(t.superadmin_earning)}</TableCell>}
                <TableCell className="text-right font-mono text-success font-medium">{inr(t.merchant_net)}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(t.confirmed_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
